/**
 * Per-locale formatting registry.
 *
 * Single source of truth for how each locale renders dates, times, numbers,
 * and measurement units. As Velys expands to new countries, add ONE entry to
 * `LOCALE_FORMATTING` below — every locale-aware helper reads from this registry,
 * so there are no parallel lists to keep in sync.
 */

export interface LocaleFormatConfig {
  /** moment.js date format token, e.g. "DD.MM.YYYY". */
  dateFormat: string;
  /** BCP-47 locale passed to `toLocaleString` for number grouping/decimals. */
  numberLocale: string;
  /** Display time as 24-hour ("14:30") instead of 12-hour ("2:30 PM"). */
  use24HourTime: boolean;
  /** Use metric measurement units (kg, cm) instead of imperial (lbs, in). */
  metricUnits: boolean;
  /** Uses European decimal/thousands separators (1.234,56). */
  europeanNumberSeparators: boolean;
}

export const DEFAULT_LOCALE_FORMAT: LocaleFormatConfig = {
  dateFormat: "DD/MM/YYYY",
  numberLocale: "en-US",
  use24HourTime: false,
  metricUnits: false,
  europeanNumberSeparators: false,
};

export const LOCALE_FORMATTING: Record<string, LocaleFormatConfig> = {
  "en-US": {
    dateFormat: "MM/DD/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: false,
    europeanNumberSeparators: false,
  },
  "en-GB": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "en-AU": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: false,
    europeanNumberSeparators: false,
  },
  "en-AT": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "de-DE",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: true,
  },
  "en-CH": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "de-CH",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "en-DE": {
    dateFormat: "DD.MM.YYYY",
    numberLocale: "de-DE",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: true,
  },
  "en-IE": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "de-DE": {
    dateFormat: "DD.MM.YYYY",
    numberLocale: "de-DE",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: true,
  },
  "es-ES": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: false,
    europeanNumberSeparators: false,
  },
  "fr-FR": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: false,
    europeanNumberSeparators: false,
  },
  "ja-JP": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: false,
    europeanNumberSeparators: false,
  },
  "he-IL": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "cz-CZ": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "en-SG": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  "en-MY": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "en-US",
    use24HourTime: false,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
  // Catalogued in LOCALE_CATALOG but not enabled anywhere yet (AppConfig
  // decides). Entries exist so that turning one on in the console yields
  // correct dates/units immediately instead of the US imperial default.
  "it-IT": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "it-IT",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: true,
  },
  "pt-PT": {
    dateFormat: "DD/MM/YYYY",
    numberLocale: "pt-PT",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: true,
  },
  "zh-CN": {
    dateFormat: "YYYY/MM/DD",
    numberLocale: "zh-CN",
    use24HourTime: true,
    metricUnits: true,
    europeanNumberSeparators: false,
  },
};

/**
 * Returns the formatting config for a locale, falling back to
 * `DEFAULT_LOCALE_FORMAT` for anything not yet configured. This is the accessor
 * every locale-aware helper should use.
 */
export const getLocaleFormat = (locale: string): LocaleFormatConfig =>
  LOCALE_FORMATTING[locale] ?? DEFAULT_LOCALE_FORMAT;

const localesWhere = (
  predicate: (config: LocaleFormatConfig) => boolean
): string[] =>
  Object.entries(LOCALE_FORMATTING)
    .filter(([, config]) => predicate(config))
    .map(([locale]) => locale);

/** Locales that display 24-hour time and metric units. Derived from the registry. */
export const METRIC_24H_LOCALES = localesWhere((c) => c.use24HourTime);

/** Locales that use European decimal/thousands separators. Derived from the registry. */
export const SWAPPED_NUM_LOCALES = localesWhere(
  (c) => c.europeanNumberSeparators
);
